// Spool Table view (Detailed mode). Every spool × every stage in one
// grid: a column per stage with the 8 welding bays collapsed into a
// single WLD column (a spool runs one bay). Click a stage cell to
// advance it (pending → in-progress → complete → pending). Start +
// due dates and the current-stage assignee are editable inline.
//
// Renders inside SpoolTable so it shares the selection set + bulk-
// print bar. Stage/assignee edits go through the same patchStage
// path as the cards (immediate persist + refetch); start/due dates
// for unsaved drafts fall back to the draft fields.
import { useMemo } from "react";
import type {
  SpoolStageDto,
  StageDefDto,
  StageKey,
  StageStatus,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "./StagesProvider";
import { addDays, daysBetween, fmtDate, isoToday } from "../lib/dates";
import {
  currentStage,
  denseStages,
  inferWeldBayKey,
  progressionStages,
  spoolHealth,
} from "../lib/stageHelpers";
import type { SpoolDraft } from "./spoolDraft";

type GridCol =
  | { kind: "stage"; stageKey: string; short: string; label: string; color: string }
  | { kind: "weld"; short: "WLD"; label: "Welding" };

interface Props {
  spools: SpoolDraft[];
  stagesBySpool?: Map<number, SpoolStageDto[]>;
  weldBayBySpool?: Map<number, StageKey | null>;
  team: TeamMemberDto[];
  jobId?: number;
  // Numeric job-default bay (1..8). Needed for the per-row weld-bay
  // fallback so a spool not present in weldBayBySpool (a fresh
  // draft, or a CSV-imported spool with no persisted weld row yet)
  // still resolves to the job's bay rather than rendering a "not
  // used on this spool" dash in the WLD column.
  jobBay?: number | null;
  selected: Set<number>;
  onToggleSelect(id: number, checked: boolean): void;
  onUpdate(index: number, patch: Partial<SpoolDraft>): void;
  onStagePatch(
    spoolId: number | undefined,
    stageKey: StageKey,
    patch: {
      hours?: number;
      assigneeId?: number | null;
      startDate?: string | null;
      doneDate?: string | null;
      status?: StageStatus;
    },
  ): Promise<void>;
}

const HEALTH_STRIPE: Record<string, string> = {
  overdue: "#B3261E",
  "at-risk": "#C77700",
  "on-track": "#2E7D32",
  complete: "#1B449C",
};

const CYCLE: Record<StageStatus, StageStatus> = {
  pending: "in-progress",
  "in-progress": "complete",
  complete: "pending",
  skipped: "skipped",
};

export function SpoolGridTable({
  spools,
  stagesBySpool,
  weldBayBySpool,
  team,
  jobId,
  jobBay,
  selected,
  onToggleSelect,
  onUpdate,
  onStagePatch,
}: Props) {
  const catalogue = useActiveStages();
  const today = isoToday();
  const canPersist = typeof jobId === "number";

  // Columns: each non-weld stage gets its own column; the welding
  // bays collapse into a single WLD column inserted at the position
  // of the first weld bay in catalogue order.
  const cols = useMemo<GridCol[]>(() => {
    const out: GridCol[] = [];
    let weldDone = false;
    for (const def of catalogue) {
      if (def.weld) {
        if (!weldDone) { out.push({ kind: "weld", short: "WLD", label: "Welding" }); weldDone = true; }
        continue;
      }
      out.push({ kind: "stage", stageKey: def.stageKey, short: def.short, label: def.name, color: def.color });
    }
    return out;
  }, [catalogue]);

  return (
    <div style={{ overflowX: "auto", border: "1px solid var(--bsp-border)", borderRadius: "var(--bsp-r-md, 6px)", boxShadow: "var(--bsp-shadow-card)" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
        <thead>
          <tr style={{ background: "var(--bsp-surface-muted)" }}>
            <th style={thStyle(26)} />
            <th style={thStyle(160)}>Spool No</th>
            {cols.map((c, i) => (
              <th key={c.kind === "stage" ? c.stageKey : `weld-${i}`} style={{ ...thStyle(44), textAlign: "center" }} title={c.label}>
                {c.short}
              </th>
            ))}
            <th style={thStyle(130)}>Start date</th>
            <th style={thStyle(130)}>Due date</th>
            <th style={thStyle(150)}>Assigned to</th>
            <th style={{ ...thStyle(44), textAlign: "right" }}>%</th>
          </tr>
        </thead>
        <tbody>
          {spools.map((sp, i) => (
            <GridRow
              key={sp.localKey}
              spool={sp}
              index={i}
              cols={cols}
              catalogue={catalogue}
              stages={typeof sp.id === "number" ? stagesBySpool?.get(sp.id) ?? [] : []}
              weldBayKey={typeof sp.id === "number" ? weldBayBySpool?.get(sp.id) ?? null : null}
              jobBay={jobBay ?? null}
              team={team}
              today={today}
              canPersist={canPersist}
              selected={typeof sp.id === "number" && selected.has(sp.id)}
              onToggleSelect={onToggleSelect}
              onUpdate={onUpdate}
              onStagePatch={onStagePatch}
            />
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)", margin: "8px 10px", fontFamily: "var(--bsp-font-mono)" }}>
        Click a stage cell to advance it: pending → in-progress → complete. WLD shows the spool's welding bay.
      </div>
    </div>
  );
}

function GridRow({
  spool,
  index,
  cols,
  catalogue,
  stages,
  weldBayKey: weldBayKeyProp,
  jobBay,
  team,
  today,
  canPersist,
  selected,
  onToggleSelect,
  onUpdate,
  onStagePatch,
}: {
  spool: SpoolDraft;
  index: number;
  cols: GridCol[];
  catalogue: StageDefDto[];
  stages: SpoolStageDto[];
  weldBayKey: StageKey | null;
  jobBay: number | null;
  team: TeamMemberDto[];
  today: string;
  canPersist: boolean;
  selected: boolean;
  onToggleSelect(id: number, checked: boolean): void;
  onUpdate(index: number, patch: Partial<SpoolDraft>): void;
  onStagePatch: Props["onStagePatch"];
}) {
  const weldBayKey = weldBayKeyProp ?? inferWeldBayKey(stages, jobBay);
  const dense = denseStages(stages, weldBayKey, undefined, catalogue);
  const byKey = new Map(dense.map((s) => [s.stageKey, s]));
  const progression = progressionStages(dense, weldBayKey);
  const cur = currentStage(progression);
  const completeCount = progression.filter((s) => s.status === "complete").length;
  const pct = progression.length > 0 ? Math.round((completeCount / progression.length) * 100) : 0;
  const health = spoolHealth({ dueDate: spool.dueDate ?? null, stages: dense, todayIso: today });
  const earliestStart = progression
    .map((s) => s.startDate)
    .filter((d): d is string => Boolean(d))
    .sort()[0] ?? null;
  const overdue = !!spool.dueDate && spool.dueDate < today && health !== "complete";

  const stageByCatalogue = new Map(catalogue.map((d) => [d.stageKey, d]));

  // Click a stage cell → simple cycle. Weld-bay cell cycles the
  // chosen bay's row.
  const cycle = async (stageKey: StageKey) => {
    if (!canPersist) return;
    const st = byKey.get(stageKey);
    if (!st || st.status === "skipped") return;
    const next = CYCLE[st.status];
    await onStagePatch(spool.id, stageKey, {
      status: next,
      startDate: next === "in-progress" && !st.startDate ? today : st.startDate,
    });
  };

  // Editing Start shifts every dated active stage by the same delta
  // (cascade preserved); an unscheduled spool anchors its first
  // active stage.
  const onStartChange = async (v: string) => {
    if (!v || !canPersist) return;
    if (earliestStart) {
      const delta = daysBetween(earliestStart, v);
      if (delta !== 0) {
        for (const st of progression) {
          if (st.startDate) await onStagePatch(spool.id, st.stageKey as StageKey, { startDate: addDays(st.startDate, delta) });
        }
      }
    } else {
      const first = progression[0];
      if (first) await onStagePatch(spool.id, first.stageKey as StageKey, { startDate: v });
    }
  };

  const onAssigneeChange = async (v: string) => {
    if (!cur || !canPersist) return;
    const id = v === "" ? null : Number(v);
    await onStagePatch(spool.id, cur.stageKey as StageKey, { assigneeId: id });
  };

  return (
    <tr
      style={{ borderBottom: "1px solid var(--bsp-border)" }}
      onMouseOver={(e) => { e.currentTarget.style.background = "var(--bsp-page-bg, #fafafa)"; }}
      onMouseOut={(e) => { e.currentTarget.style.background = ""; }}
    >
      <td style={{ ...tdStyle, textAlign: "center", boxShadow: `inset 3px 0 0 ${HEALTH_STRIPE[health] ?? "transparent"}` }}>
        {typeof spool.id === "number" ? (
          <input type="checkbox" checked={selected} onChange={(e) => onToggleSelect(spool.id as number, e.target.checked)} title="Select for bulk print" />
        ) : null}
      </td>
      <td style={tdStyle}>
        <div style={{ fontFamily: "var(--bsp-font-mono)", fontWeight: 700 }}>{spool.num || "—"}</div>
        {spool.description ? (
          <div style={{ fontSize: 10, color: "var(--bsp-fg-muted)" }}>{spool.description}</div>
        ) : null}
      </td>
      {cols.map((c, ci) => {
        const st = c.kind === "stage"
          ? byKey.get(c.stageKey)
          : weldBayKey
            ? byKey.get(weldBayKey)
            : undefined;
        const def = c.kind === "stage"
          ? stageByCatalogue.get(c.stageKey)
          : st
            ? stageByCatalogue.get(st.stageKey)
            : undefined;
        const key = c.kind === "stage" ? c.stageKey : `weld-${ci}`;
        if (!st || st.status === "skipped" || !def) {
          return (
            <td key={key} style={{ ...tdStyle, textAlign: "center" }}>
              <span style={dashStyle} title={`${c.label} — not used on this spool`}>—</span>
            </td>
          );
        }
        const isCur = cur?.stageKey === st.stageKey && st.status !== "complete";
        const short = c.kind === "weld" ? (def.short || "W") : c.short;
        const tip = [def.name, st.hours ? `${st.hours}h` : null, st.startDate ? fmtDate(st.startDate) : null, st.status].filter(Boolean).join(" · ");
        return (
          <td key={key} style={{ ...tdStyle, textAlign: "center" }}>
            <button
              type="button"
              className={["pipeline-cell", `is-${st.status}`, isCur ? "is-current" : ""].filter(Boolean).join(" ")}
              style={{ background: def.color, minWidth: 30, height: 22, fontSize: 10, borderRadius: 4, color: "#fff", border: "none", cursor: canPersist ? "pointer" : "default", opacity: canPersist ? 1 : 0.7 }}
              title={tip}
              onClick={() => void cycle(st.stageKey as StageKey)}
              disabled={!canPersist}
            >
              {short}
            </button>
          </td>
        );
      })}
      <td style={tdStyle}>
        <input
          type="date"
          defaultValue={earliestStart ?? ""}
          disabled={!canPersist}
          onChange={(e) => void onStartChange(e.target.value)}
          title="Spool start — shifts every scheduled stage by the same amount"
          style={dateInputStyle(false)}
        />
      </td>
      <td style={tdStyle}>
        <input
          type="date"
          value={spool.dueDate ?? ""}
          onChange={(e) => onUpdate(index, { dueDate: e.target.value || null })}
          style={dateInputStyle(overdue)}
        />
      </td>
      <td style={tdStyle}>
        <select
          value={cur?.assigneeId ?? ""}
          disabled={!canPersist || !cur}
          onChange={(e) => void onAssigneeChange(e.target.value)}
          title={cur ? `Assignee of the current stage — ${stageByCatalogue.get(cur.stageKey)?.name ?? cur.stageKey}` : "No current stage"}
          style={{ width: "100%", padding: "3px 4px", fontSize: 11 }}
        >
          <option value="">—</option>
          {team.map((t) => (
            <option key={t.id} value={t.id}>{t.initials} · {t.name}</option>
          ))}
        </select>
      </td>
      <td style={{ ...tdStyle, textAlign: "right", fontFamily: "var(--bsp-font-mono)", fontWeight: 700 }}>{pct}%</td>
    </tr>
  );
}

const thStyle = (width: number): React.CSSProperties => ({
  padding: "7px 8px",
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase",
  color: "var(--bsp-fg-muted)",
  textAlign: "left",
  minWidth: width,
  whiteSpace: "nowrap",
});

const tdStyle: React.CSSProperties = {
  padding: "5px 8px",
  verticalAlign: "middle",
};

const dashStyle: React.CSSProperties = {
  display: "inline-block",
  minWidth: 30,
  height: 22,
  lineHeight: "20px",
  border: "1px dashed var(--bsp-border-strong)",
  borderRadius: 4,
  color: "var(--bsp-fg-muted)",
  fontSize: 11,
};

function dateInputStyle(overdue: boolean): React.CSSProperties {
  return {
    width: "100%",
    padding: "3px 4px",
    fontSize: 11,
    fontFamily: "var(--bsp-font-mono)",
    border: `1px solid ${overdue ? "var(--bsp-red-fg)" : "var(--bsp-border)"}`,
    color: overdue ? "var(--bsp-red-fg)" : "var(--bsp-fg)",
    borderRadius: 4,
  };
}
